/**
 * `/interests/:interestId` — one enquiry, its status, and its timeline.
 *
 * Where the sales desk does its work. Three things, top to bottom:
 *
 *   1. **Who and what** — the person, how to reach them, what they asked for,
 *      and where the row came from (the website form, or a staff member, named).
 *   2. **Status** — the buttons the owner asked for: mark in progress, close,
 *      and reopen, because a "no" in March is sometimes a "yes" in June. An
 *      optional line saying why goes onto the timeline with the change.
 *   3. **The timeline** — every note and every status change, oldest first,
 *      each stamped with who wrote it and when. Append-only at the database
 *      (0040): a note cannot be edited or deleted, so a mistake is corrected
 *      by writing another one. The screen says so beside the box rather than
 *      letting somebody find out by looking for an edit button.
 *
 * Every write answers with the interest as it now stands, and the screen draws
 * from that answer rather than from what it sent. A status change that changed
 * nothing — somebody else got there first — is said in words, because a
 * success toast there would claim a move nobody made.
 *
 * THE TIMELINE IS AN EVENT LOG (slice 66). `@wend/ui`'s `EventLog` draws it:
 * the time down the left, an icon tile (a speech mark for a note, an arrow in
 * violet for a status change — the same split the old dot drew, filled for a
 * status and hollow for a note), a short title, and the rest as the row's
 * detail. Still oldest first, still every entry, still who and when on each.
 * The note's own words are set at reading size in the text colour inside the
 * detail, not in the log's small muted detail type: a sales note is the thing
 * this screen is for, and it must not read as a footnote. The status move
 * keeps both statuses as Badges with "to" for a screen reader where the eye
 * sees an arrow. The labels of the facts at the top are the product's kicker.
 */
import { ArrowLeft, ArrowRightLeft, Mail, MessageSquareText, Phone, RotateCw } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { MAX_INTEREST_NOTE_LENGTH, type InterestDetail, type InterestNote, type InterestStatus } from '@wend/shared';
import {
  Badge,
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EventLog,
  FormField,
  Kicker,
  Spinner,
  useToast,
  type EventLogItem,
} from '@wend/ui';

import { AdminShell } from '../components/AdminShell';
import { ErrorState } from '../components/ErrorState';
import { FormAlert } from '../components/FormAlert';
import { Textarea } from '../components/Textarea';
import { describeApiError, toApiError, type ApiError } from '../lib/api-error';
import { formatDateTime, formatRelative } from '../lib/format';
import { INTEREST_SOURCE_LABEL, INTEREST_STATUS_LABEL, INTEREST_STATUS_TONE } from '../lib/interest-status';
import { addInterestNote, changeInterestStatus, getInterest } from '../lib/interests-api';
import { useAsyncData } from '../lib/use-async-data';

export function InterestDetailPage() {
  const { interestId } = useParams<{ interestId: string }>();
  if (interestId === undefined) return <Navigate to="/interests" replace />;
  // Keyed, so moving between interests never shows one's write answer over another.
  return <InterestView key={interestId} interestId={interestId} />;
}

const backLink = (
  <Link
    to="/interests"
    className="inline-flex items-center gap-1.5 font-mono text-[0.7rem] uppercase tracking-[0.12em] text-muted transition-colors hover:text-text"
  >
    <ArrowLeft aria-hidden="true" className="size-3.5" />
    Interests
  </Link>
);

function InterestView({ interestId }: { interestId: string }) {
  const { status, data, error, refreshing, reload } = useAsyncData(
    (signal) => getInterest(interestId, signal),
    [interestId],
  );

  /**
   * The freshest copy: a write's answer replaces the loaded one until the next
   * load, so the timeline shows the note that was just added without a second
   * round trip.
   */
  const [latest, setLatest] = useState<InterestDetail | null>(null);
  useEffect(() => {
    if (data !== null) setLatest(data);
  }, [data]);

  const interest = latest ?? data;

  if (status === 'loading' && interest === null) {
    return (
      <AdminShell title="Interest">
        <div className="flex max-w-4xl flex-col gap-5">
          {backLink}
          <Card>
            <div className="flex items-center justify-center gap-3 px-6 py-16 text-muted">
              <Spinner size="lg" label="Loading this interest" />
            </div>
          </Card>
        </div>
      </AdminShell>
    );
  }

  if (interest === null) {
    return (
      <AdminShell title="Interest">
        <div className="flex max-w-4xl flex-col gap-5">
          {backLink}
          <Card>
            {error !== null ? (
              <ErrorState error={error} title="Could not load this interest" onRetry={reload} />
            ) : (
              <div role="alert" className="flex flex-col items-start gap-3 px-6 py-10">
                <p className="text-[0.95rem] text-muted">This interest could not be loaded.</p>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  leadingIcon={<RotateCw aria-hidden="true" />}
                  onClick={reload}
                >
                  Try again
                </Button>
              </div>
            )}
          </Card>
        </div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title={interest.name}>
      <div className="flex max-w-4xl flex-col gap-5">
        {backLink}

        {status === 'error' && error !== null && (
          <FormAlert requestId={error.requestId}>
            {`${describeApiError(error)} What is shown below is the last version that loaded.`}
          </FormAlert>
        )}

        <Summary interest={interest} refreshing={refreshing} onRefresh={reload} />
        <StatusPanel interest={interest} onChanged={setLatest} />
        <Timeline interest={interest} onNoted={setLatest} />
      </div>
    </AdminShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Who and what                                                                */
/* -------------------------------------------------------------------------- */

function Summary({
  interest,
  refreshing,
  onRefresh,
}: {
  interest: InterestDetail;
  refreshing: boolean;
  /** Re-reads from the API — to see a colleague's note, or after a failed read. */
  onRefresh: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <Badge tone={INTEREST_STATUS_TONE[interest.status]}>{INTEREST_STATUS_LABEL[interest.status]}</Badge>
          <Badge>{INTEREST_SOURCE_LABEL[interest.source]}</Badge>
          {refreshing ? (
            <Spinner size="sm" label="Re-reading this interest" />
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="ml-auto"
              leadingIcon={<RotateCw aria-hidden="true" />}
              onClick={onRefresh}
            >
              Refresh
            </Button>
          )}
        </div>
        <CardTitle>
          {interest.name} <span className="font-normal text-muted">· {interest.organisation}</span>
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-col gap-5">
        <dl className="flex flex-wrap gap-x-10 gap-y-4">
          <Fact label="Reach them">
            {interest.email !== null && (
              <a
                href={`mailto:${interest.email}`}
                className="inline-flex items-center gap-1.5 font-mono text-[0.82rem] underline-offset-4 hover:underline"
              >
                <Mail aria-hidden="true" className="size-3.5 text-muted" />
                {interest.email}
              </a>
            )}
            {interest.phone !== null && (
              <a
                href={`tel:${interest.phone}`}
                className="flex items-center gap-1.5 font-mono text-[0.82rem] underline-offset-4 hover:underline"
              >
                <Phone aria-hidden="true" className="size-3.5 text-muted" />
                {interest.phone}
              </a>
            )}
          </Fact>

          <Fact label="Received">
            <span className="whitespace-nowrap">{formatDateTime(interest.createdAt)}</span>
            <span className="block text-[0.82rem] text-muted">
              {interest.source === 'site'
                ? `${formatRelative(interest.createdAt)}, from the website form`
                : `${formatRelative(interest.createdAt)}, added by ${interest.createdByEmail ?? 'staff'}`}
            </span>
          </Fact>

          <Fact label="Status since">
            <span className="whitespace-nowrap">{formatDateTime(interest.statusChangedAt)}</span>
            <span className="block text-[0.82rem] text-muted">{formatRelative(interest.statusChangedAt)}</span>
          </Fact>
        </dl>

        <div className="flex flex-col gap-1.5">
          <Kicker>What they asked for</Kicker>
          {interest.message === '' ? (
            <p className="text-[0.92rem] italic text-muted">They left no message.</p>
          ) : (
            <p className="max-w-prose whitespace-pre-wrap break-words text-[0.95rem] leading-relaxed">
              {interest.message}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-w-[10rem] flex-col gap-1.5">
      <Kicker as="dt">{label}</Kicker>
      <dd className="text-[0.92rem] leading-6">{children}</dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

/** The moves offered from each status, in the order they are drawn. */
const MOVES: Readonly<Record<InterestStatus, readonly { to: InterestStatus; label: string }[]>> = {
  new: [
    { to: 'in_progress', label: 'Mark in progress' },
    { to: 'closed', label: 'Close' },
  ],
  in_progress: [
    { to: 'closed', label: 'Close' },
    { to: 'new', label: 'Back to new' },
  ],
  closed: [{ to: 'in_progress', label: 'Reopen' }],
};

/** The toast after a move, written per destination so "Back to new" does not read "Now new". */
const MOVED_TITLE: Readonly<Record<InterestStatus, string>> = {
  new: 'Moved back to new',
  in_progress: 'Marked in progress',
  closed: 'Closed',
};

function StatusPanel({
  interest,
  onChanged,
}: {
  interest: InterestDetail;
  onChanged: (next: InterestDetail) => void;
}) {
  const { toast } = useToast();
  const [reason, setReason] = useState('');
  const [pending, setPending] = useState<InterestStatus | null>(null);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [unchanged, setUnchanged] = useState<InterestStatus | null>(null);

  const move = async (to: InterestStatus): Promise<void> => {
    setPending(to);
    setFailure(null);
    setUnchanged(null);
    try {
      const trimmed = reason.trim();
      const result = await changeInterestStatus(interest.id, {
        status: to,
        ...(trimmed === '' ? {} : { note: trimmed }),
      });
      onChanged(result.interest);
      if (result.changed) {
        setReason('');
        toast({ tone: 'success', title: MOVED_TITLE[to] });
      } else {
        setUnchanged(to);
      }
    } catch (error: unknown) {
      setFailure(toApiError(error));
    } finally {
      setPending(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Status</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {failure !== null && <FormAlert requestId={failure.requestId}>{describeApiError(failure)}</FormAlert>}
        {unchanged !== null && (
          <Callout tone="info" live="assertive" title="Nothing changed">
            <p>
              It was already {INTEREST_STATUS_LABEL[unchanged].toLowerCase()} — somebody else probably got there
              first. Nothing was written, including the line below, which is still here if you want it as a note.
            </p>
          </Callout>
        )}

        <FormField
          label="Why (optional)"
          description="Saved on the timeline with the status change."
        >
          <Textarea
            rows={2}
            value={reason}
            maxLength={MAX_INTEREST_NOTE_LENGTH}
            placeholder="e.g. Demo booked for Thursday"
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
        </FormField>

        <div className="flex flex-wrap gap-2.5">
          {MOVES[interest.status].map((option, index) => (
            <Button
              key={option.to}
              type="button"
              size="sm"
              variant={index === 0 ? 'primary' : 'secondary'}
              loading={pending === option.to}
              disabled={pending !== null && pending !== option.to}
              onClick={() => {
                void move(option.to);
              }}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Timeline                                                                    */
/* -------------------------------------------------------------------------- */

function Timeline({
  interest,
  onNoted,
}: {
  interest: InterestDetail;
  onNoted: (next: InterestDetail) => void;
}) {
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<ApiError | null>(null);

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const trimmed = body.trim();
    if (trimmed === '' || saving) return;

    setSaving(true);
    setFailure(null);
    try {
      onNoted(await addInterestNote(interest.id, { body: trimmed }));
      setBody('');
    } catch (error: unknown) {
      // What they typed stays in the box: losing a sales note to a dropped
      // connection is exactly the failure this screen must not have.
      setFailure(toApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Notes and history</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {interest.notes.length === 0 ? (
          <p className="text-[0.9rem] text-muted">
            Nothing yet. Write down the first conversation below — who you spoke to and what happens next.
          </p>
        ) : (
          <EventLog label="Oldest first" whenWidth="date" items={interest.notes.map(timelineItem)} />
        )}

        <form
          onSubmit={(event) => {
            void submit(event);
          }}
          className="flex flex-col gap-3 border-t border-line pt-5"
        >
          {failure !== null && <FormAlert requestId={failure.requestId}>{describeApiError(failure)}</FormAlert>}

          <FormField
            label="Add a note"
            description="Stamped with your email and the time. Notes cannot be edited or deleted — correct one by adding another."
          >
            <Textarea
              rows={3}
              value={body}
              maxLength={MAX_INTEREST_NOTE_LENGTH}
              placeholder="Called Priya — wants pricing for 20k contacts, follow up Monday"
              onChange={(event) => {
                setBody(event.target.value);
              }}
            />
          </FormField>

          <div>
            <Button type="submit" size="sm" loading={saving} disabled={body.trim() === ''}>
              Add note
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

/** One note or status change as a log row. See the file header for the shape. */
function timelineItem(note: InterestNote): EventLogItem {
  const isMove = note.kind === 'status' && note.fromStatus !== null && note.toStatus !== null;
  const full = formatDateTime(note.createdAt);
  const cut = full.lastIndexOf(', ');

  return {
    key: note.id,
    when: (
      <time dateTime={note.createdAt} className="flex flex-col leading-snug">
        <span className="whitespace-nowrap">{cut === -1 ? full : full.slice(0, cut)}</span>
        {cut !== -1 && <span className="whitespace-nowrap">{full.slice(cut + 2)}</span>}
      </time>
    ),
    tone: isMove ? 'brand' : 'neutral',
    icon: isMove ? <ArrowRightLeft aria-hidden="true" /> : <MessageSquareText aria-hidden="true" />,
    title: isMove ? 'Status changed' : 'Note',
    aside: <span className="font-mono text-[0.68rem] tracking-[0.04em]">{note.adminEmail}</span>,
    detail: (
      <span className="mt-1 flex flex-col gap-1.5">
        {isMove && note.fromStatus !== null && note.toStatus !== null && (
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={INTEREST_STATUS_TONE[note.fromStatus]}>{INTEREST_STATUS_LABEL[note.fromStatus]}</Badge>
            <span aria-hidden="true" className="text-brand-ink">
              →
            </span>
            <span className="sr-only">to</span>
            <Badge tone={INTEREST_STATUS_TONE[note.toStatus]}>{INTEREST_STATUS_LABEL[note.toStatus]}</Badge>
          </span>
        )}
        {note.body !== null && (
          <span className="max-w-prose whitespace-pre-wrap break-words text-[0.9rem] leading-relaxed text-text">
            {note.body}
          </span>
        )}
      </span>
    ),
  };
}
